import { useState, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import { useMutation } from "util/graphql";
import Button from "common/core/button";
import { transactionEditRoute } from "util/routes";

import Styles from "./button.module.scss";
import DuplicateOrganizationTransactionMutation from "./duplicate_organization_transaction_mutation.graphql";

interface Props {
  transactionId: string;
}

const DuplicateTransactionButton = ({ transactionId }: Props) => {
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [mutationFailed, setMutationFailed] = useState(false);

  const duplicateOrganizationTransactionMutation = useMutation(
    DuplicateOrganizationTransactionMutation,
  );

  const navigate = useNavigate();

  const duplicateTransaction = useCallback(() => {
    setIsDuplicating(true);

    duplicateOrganizationTransactionMutation({
      variables: {
        input: {
          id: transactionId,
        },
      },
    })
      .then((result) => {
        if (result.data?.duplicateOrganizationTransaction?.organizationTransaction.id) {
          navigate(
            transactionEditRoute({
              id: result.data.duplicateOrganizationTransaction.organizationTransaction.id,
            }),
          );
        } else {
          setIsDuplicating(false);
          setMutationFailed(true);
        }
      })
      .catch(() => {
        setIsDuplicating(false);
        setMutationFailed(true);
      });
  }, [transactionId]);

  const buttonInner = useCallback(() => {
    if (mutationFailed) {
      return (
        <FormattedMessage
          id="1887362b-66dd-49af-a699-1f8305d195d3"
          defaultMessage="Error duplicating"
        />
      );
    }
    return (
      <span>
        <FormattedMessage id="bb9fc25a-d252-40e2-87d0-5db3c95de3b6" defaultMessage="Duplicate" />
        <span className={Styles.button}>
          <FormattedMessage id="2bbf3bd8-0b2f-4888-8635-1fe5042e2709" defaultMessage="Internal" />
        </span>
      </span>
    );
  }, []);

  return (
    <Button
      variant={!isDuplicating && !mutationFailed ? "secondary" : "primary"}
      isLoading={isDuplicating}
      onClick={duplicateTransaction}
      buttonColor={mutationFailed ? "danger" : "action"}
      data-automation-id="duplicate-transaction-button"
    >
      {buttonInner()}
    </Button>
  );
};

export default DuplicateTransactionButton;
